import type { ModButtonProps } from '@moderneinc/design-system-components';
import { ModButton } from '@moderneinc/design-system-components';
import { IconButton, type IconButtonProps } from '@mui/material';
import type { FunctionComponent } from 'react';

import { ClipBoardIcon } from './clip-board-icon.component';
import { CopyToClipboardWithTooltip } from './copy-to-clipboard-with-tooltip.component';

export type CopyPastaProps =
  | {
      buttonGroupCompatible?: boolean;
      buttonProps?: ModButtonProps;
      buttonText?: string;
      onCopy?: () => void;
      text: string;
      getText?: undefined;
      tooltip?: string;
      disableClipboard?: boolean;
      iconPosition?: 'start' | 'end';
    }
  | {
      buttonGroupCompatible?: boolean;
      buttonProps?: ModButtonProps;
      buttonText?: string;
      onCopy?: () => void;
      text?: undefined;
      getText: () => string;
      tooltip?: string;
      disableClipboard?: boolean;
      iconPosition?: 'start' | 'end';
    };

export const CopyPasta: FunctionComponent<CopyPastaProps> = ({
  buttonProps,
  buttonText = null,
  buttonGroupCompatible = false,
  onCopy = undefined,
  text,
  getText,
  tooltip = 'Copy to clipboard',
  disableClipboard = false,
  iconPosition = 'start',
  ...rest
}) => {
  const testId = 'copy-button';

  // Destructure Button-specific props that aren't valid on IconButton
  const {
    startIcon,
    endIcon,
    variant: _variant,
    href: _href,
    loading: _loading,
    ...iconButtonProps
  } = buttonProps || {};

  if (!text && !getText) {
    return null;
  }

  const copyText = text || getText();

  if (disableClipboard) {
    return null;
  }

  if (buttonText) {
    return (
      <CopyToClipboardWithTooltip
        text={copyText}
        onCopy={onCopy}
        tooltip={tooltip}
      >
        {(copied) => (
          <ModButton
            data-testid={testId}
            {...buttonProps}
            {...rest}
            startIcon={
              iconPosition === 'start'
                ? (startIcon ?? <ClipBoardIcon isCopied={copied} />)
                : undefined
            }
            endIcon={
              iconPosition === 'end'
                ? (endIcon ?? <ClipBoardIcon isCopied={copied} />)
                : undefined
            }
          >
            {buttonText}
          </ModButton>
        )}
      </CopyToClipboardWithTooltip>
    );
  }

  return (
    <CopyToClipboardWithTooltip
      text={copyText}
      onCopy={onCopy}
      tooltip={tooltip}
    >
      {(copied) =>
        buttonGroupCompatible ? (
          <ModButton data-testid={testId} {...buttonProps} {...rest}>
            <ClipBoardIcon isCopied={copied} />
          </ModButton>
        ) : (
          <IconButton
            data-testid={testId}
            size="small"
            style={{
              color: 'inherit',
              backgroundColor: 'transparent'
            }}
            {...(iconButtonProps as Partial<IconButtonProps>)}
          >
            <ClipBoardIcon isCopied={copied} />
          </IconButton>
        )
      }
    </CopyToClipboardWithTooltip>
  );
};
